import { Dropzone, Footer, Header, Hero } from "@batchworks/ui";
import { MotionConfig } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { AUDIO_BRAND, AUDIO_THEME, HERO_FEATURES } from "./appConfig";
import ConverterDashboard from "./components/ConverterDashboard";
import {
  ACCEPTED_EXT,
  formatBytes,
  isOversizedAudioFile,
  isSupportedAudioFile,
  MAX_AUDIO_BYTES,
} from "./utils/audioOps";

function App() {
  const [darkMode, setDarkMode] = useState(
    () =>
      globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false,
  );
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const addFiles = useCallback((newFiles: File[]) => {
    setFiles((prev) => {
      const existing = new Set(prev.map((file) => file.name + file.size));
      return [
        ...prev,
        ...newFiles.filter((file) => !existing.has(file.name + file.size)),
      ];
    });
  }, []);

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-slate-50 text-slate-900 transition-colors duration-300 dark:bg-slate-950 dark:text-slate-100">
      <MotionConfig reducedMotion="user">
        <a
          href="#main-content"
          className={`sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-slate-900 focus:shadow-lg focus-visible:outline-none focus-visible:ring-2 ${AUDIO_THEME.focusRing} dark:focus:bg-slate-900 dark:focus:text-white`}
        >
          Skip to converter
        </a>

        <Header
          brand={AUDIO_BRAND}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          theme={AUDIO_THEME}
        />

        <main id="main-content" className="flex-grow px-4 py-6 md:py-10">
          <Hero
            badge="Free · Private · No uploads"
            title="Free Batch Audio Converter for"
            highlightedTitle="WAV, MP3, M4A, FLAC & OGG"
            description="Convert WAV, MP3, M4A, FLAC, OGG and WEBM audio in bulk, directly in your browser. Your files stay local, with no uploads, accounts, or server processing."
            detail="Supports WAV · MP3 · M4A · AAC · FLAC · OGG · OPUS · WEBM with bitrate, sample-rate and channel settings."
            features={HERO_FEATURES}
            theme={AUDIO_THEME}
          />
          <Dropzone
            onFilesAdded={addFiles}
            acceptedExt={ACCEPTED_EXT}
            formatPills={["WAV", "MP3", "M4A", "AAC", "FLAC", "OGG", "WEBM"]}
            maxBytes={MAX_AUDIO_BYTES}
            formatBytes={formatBytes}
            isSupportedFile={isSupportedAudioFile}
            isOversizedFile={isOversizedAudioFile}
            copy={{
              ariaLabel: "Add audio files to convert",
              draggingTitle: "Release to add files",
              idleTitle: "Drop your audio here",
              browseLabel: "click to browse",
              privacyLine: "Private by design",
            }}
            theme={AUDIO_THEME}
          />
          <ConverterDashboard files={files} setFiles={setFiles} />
        </main>

        <Footer
          brand={AUDIO_BRAND}
          description="is a free browser-based batch audio converter for WAV, MP3, M4A, FLAC, OGG and WEBM."
          theme={AUDIO_THEME}
        />
      </MotionConfig>
    </div>
  );
}

export default App;
